import { EntityRoutes } from 'routes/constants';
import { hasSingleRole } from 'services/Authorization';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { formatDuration, hasFeatureFlag } from 'UI/utils';

const COPIES = strings.commandCenter.overallPerformance.columns;

const LAST_DOC_COLUMNS = !hasSingleRole(Roles.Recruiter)
  ? [
      {
        name: 'lastFeedbackNoteDate',
        label: COPIES.lastDocDate,
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackNoteGiverName',
        label: COPIES.lastDocBy,
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

const SUPPORT_ACTIVITY_COLUMNS = !hasSingleRole(Roles.Recruiter)
  ? [
      {
        name: 'lastFeedbackActivityType',
        label: COPIES.supportActivityType,
        options: {
          sort: true,
          display: true
        }
      },
      {
        name: 'lastFeedbackActivityDate',
        label: COPIES.supportActivityDate,
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackActivityGiverName',
        label: COPIES.supportActivityBy,
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

const ACTIVE_COLUMNS = [
  {
    name: 'activeSendouts',
    label: COPIES.currentActiveSendouts,
    options: {
      sort: true,
      display: true
    }
  }
];

const TRACKING_CASH_IN_COLUMNS = hasFeatureFlag(FeatureFlags.CommandCenterTrackingCashIn)
  ? [
      {
        name: 'trackingCashIn',
        label: COPIES.trackingCashIn,
        options: {
          sort: true,
          display: true,
          renderer: 'currency_fixed'
        }
      }
    ]
  : [];

export const OVERALL_PERFORMANCE_COLUMNS = [
  {
    name: 'idFortpac',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'empNo',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'recruiterName',
    label: COPIES.name,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewRecruiter,
          idKey: 'empNo',
          textKey: 'name',
          enableInTabRedirect: true
        }
      },
      setCellHeaderProps: () => ({ className: 'dataTableStickyLeftColumnHeader' }),
      setCellProps: () => ({
        className: 'dataTableStickyLeftColumn'
      })
    }
  },
  {
    name: 'team',
    label: COPIES.team,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewTeam,
          idKey: 'managerEmpNo',
          textKey: 'team',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'healthStatus',
    label: COPIES.currentHealthStatus,
    options: {
      sort: true,
      display: true,
      renderer: 'healthStatusChip'
    }
  },
  {
    name: 'hireDate',
    label: COPIES.startDate,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'employeeStatus',
    label: COPIES.recruiterStatus,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'directorName',
    label: COPIES.director,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'teamSupervisorsNames',
    label: COPIES.supportTeam,
    options: {
      sort: false,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 200 }
      })
    }
  },
  {
    name: 'tenure',
    label: COPIES.tenure,
    options: {
      sort: true,
      display: true,
      renderer: 'tenure'
    }
  },
  {
    name: 'daysInStatus',
    label: COPIES.daysInStatus,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'inMails',
    label: COPIES.inMails,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'inMails',
          goalKey: 'inMailsGoals'
        }
      }
    }
  },
  {
    name: 'avgInMails',
    label: COPIES.avgDailyInMails,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'avgInMails',
          goalKey: 'avgInMailsGoals'
        }
      }
    }
  },
  {
    name: 'bulks',
    label: COPIES.bulks,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'bulks',
          goalKey: 'bulksGoal'
        }
      }
    }
  },
  {
    name: 'peopleReached',
    label: COPIES.peopleReached,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'peopleReached',
          goalKey: 'peopleReachedGoal'
        }
      }
    }
  },
  {
    name: 'todayLivePosts',
    label: COPIES.livePosts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'avgTodayLivePosts',
    label: COPIES.avgDailyActivePosts,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'newLivePosts',
    label: COPIES.newPosts,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'calls',
    label: COPIES.calls,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'calls',
          goalKey: 'callsGoal'
        }
      }
    }
  },
  {
    name: 'callsTime',
    label: COPIES.phoneTime,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'callsTime',
          goalKey: 'callsTimeGoal',
          formatter: formatDuration
        }
      }
    }
  },
  {
    name: 'sendouts',
    label: COPIES.sendouts,
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'sendouts',
          goalKey: 'sendoutsGoal'
        }
      }
    }
  },
  {
    name: 'sendovers',
    label: COPIES.sendovers,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'placements',
    label: COPIES.placements,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSent',
    label: COPIES.feeAgreementsSent,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSigned',
    label: COPIES.signedAgreements,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notSignedCompaniesJo',
    label: COPIES.notSignedCompaniesJobOrders,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'signedCompaniesJo',
    label: COPIES.signedCompaniesJobOrders,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'candidatesAdded',
    label: COPIES.candidates,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'contactsAdded',
    label: COPIES.contacts,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'haAdded',
    label: COPIES.hiringAuthorities,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'companiesAdded',
    label: COPIES.companies,
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'estimates',
    label: COPIES.estimates,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'avgFeePercentage',
    label: COPIES.avgFeePercentage,
    options: {
      sort: true,
      display: true,
      renderer: 'percentage'
    }
  },
  {
    name: 'avgFeeMoney',
    label: COPIES.avgFee,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'billings',
    label: COPIES.billings,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'production',
    label: COPIES.production,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashIn',
    label: COPIES.cashIn,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'outStandingBillings',
    label: COPIES.currentOutstandingBillings,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'drawBalance',
    label: COPIES.currentDrawBalance,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'productionYTD',
    label: COPIES.productionYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashInYTD',
    label: COPIES.cashInYTD,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'careerCashIn',
    label: COPIES.careerCashIn,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'trackingProduction',
    label: COPIES.trackingProduction,
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...TRACKING_CASH_IN_COLUMNS,
  ...SUPPORT_ACTIVITY_COLUMNS,
  {
    name: 'recruitedBy',
    label: COPIES.recruitedBy,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner1Name',
    label: COPIES.channelPartner1,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner2Name',
    label: COPIES.channelPartner2,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'termDate',
    label: COPIES.termDate,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'location',
    label: COPIES.location,
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'countryCode',
    label: COPIES.countryCode,
    options: {
      sort: true,
      display: true
    }
  },
  ...ACTIVE_COLUMNS,
  ...LAST_DOC_COLUMNS
];
